import type { ApiErrorPayload, IconResult, InventorySnapshot, SessionUser } from '../shared/types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(message: string, status: number, code = 'UNKNOWN', requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (requestId !== undefined) this.requestId = requestId;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
    });
  } catch {
    throw new ApiError('Unable to reach the server. Check your connection and try again.', 0, 'NETWORK');
  }
  if (!response.ok) {
    const fallback: ApiErrorPayload = { error: 'The request could not be completed.', code: 'UNKNOWN' };
    const payload = await response.json().catch(() => fallback) as Partial<ApiErrorPayload>;
    throw new ApiError(payload.error ?? fallback.error, response.status, payload.code ?? fallback.code, payload.requestId);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  async session(): Promise<SessionUser | null> {
    try {
      const result = await request<{ authenticated: boolean; user?: SessionUser }>('/api/session');
      return result.authenticated ? result.user ?? null : null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  async login(username: string, password: string): Promise<SessionUser> {
    const result = await request<{ user: SessionUser }>('/api/session/login', {
      method: 'POST', body: JSON.stringify({ username, password }),
    });
    return result.user;
  },
  logout: () => request<void>('/api/session', { method: 'DELETE' }),
  inventory: (fresh = false) => request<InventorySnapshot>(`/api/inventory${fresh ? '?fresh=1' : ''}`),
  adjust: (variationId: string, locationId: string, delta: number, operationId: string) =>
    request<void>('/api/inventory/adjust', { method: 'POST', body: JSON.stringify({ variationId, locationId, delta, operationId }) }),
  setStock: (variationId: string, locationId: string, quantity: number, operationId: string) =>
    request<void>('/api/inventory/stock', { method: 'PUT', body: JSON.stringify({ variationId, locationId, quantity, operationId }) }),
  updateItem: (itemId: string, name: string, description: string, operationId: string) =>
    request<void>(`/api/catalog/items/${encodeURIComponent(itemId)}`, {
      method: 'PUT', body: JSON.stringify({ name, description, operationId }),
    }),
  async icons(items: Array<{ itemId: string; name: string; category?: string }>) {
    const batches: Array<Array<{ itemId: string; name: string; category?: string }>> = [];
    for (let offset = 0; offset < items.length; offset += 50) {
      batches.push(items.slice(offset, offset + 50));
    }
    const responses = await Promise.all(batches.map((batch) =>
      request<{ icons: IconResult[] }>('/api/icons/batch', {
        method: 'POST',
        body: JSON.stringify({ items: batch }),
      }),
    ));
    return { icons: responses.flatMap((response) => response.icons) };
  },
};
